import { TrashIcon } from "lucide-react";
import { useRef } from "react";
import { Badge, Button, Input, Textarea } from "@devhop/ui";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type {
  QiCatalogProductDTO,
  QiCatalogRawMaterialDTO,
  QiCatalogServiceDTO,
} from "../api/catalog-types";
import {
  resolveQiProductPriceHint,
  resolveQiProductUnitPrice,
  resolveQiRawMaterialPriceHint,
  resolveQiRawMaterialUnitPrice,
  resolveQiServicePriceHint,
  resolveQiServiceUnitPrice,
} from "../lib/qi-line-pricing";
import { QiProductPicker } from "./QiProductPicker";
import { QiRawMaterialPicker } from "./QiRawMaterialPicker";
import { QiServicePicker } from "./QiServicePicker";
import { QiLineAvailabilityBadge } from "./QiLineAvailabilityBadge";

export type QiLineItemRow = {
  id: string;
  lineKind: "product" | "raw_material" | "service" | "free_text";
  productId: string;
  rawMaterialId: string;
  serviceId: string;
  catalogLabel: string;
  description: string;
  detail: string;
  quantity: number;
  unit: string;
  unitPrice: number;
};

type QiCatalogSelection =
  | { kind: "product"; item: QiCatalogProductDTO }
  | { kind: "raw_material"; item: QiCatalogRawMaterialDTO }
  | { kind: "service"; item: QiCatalogServiceDTO };

function resolveCatalogUnitPrice(sel: QiCatalogSelection, qty: number) {
  switch (sel.kind) {
    case "product":
      return resolveQiProductUnitPrice(sel.item, qty);
    case "raw_material":
      return resolveQiRawMaterialUnitPrice(sel.item, qty);
    case "service":
      return resolveQiServiceUnitPrice(sel.item, qty);
  }
}

function resolveCatalogPriceHint(sel: QiCatalogSelection, qty: number) {
  switch (sel.kind) {
    case "product":
      return resolveQiProductPriceHint(sel.item, qty);
    case "raw_material":
      return resolveQiRawMaterialPriceHint(sel.item, qty);
    case "service":
      return resolveQiServicePriceHint(sel.item, qty);
  }
}

export function createEmptyQiLineItem(id: string): QiLineItemRow {
  return {
    id,
    lineKind: "product",
    productId: "",
    rawMaterialId: "",
    serviceId: "",
    catalogLabel: "",
    description: "",
    detail: "",
    quantity: 1,
    unit: "",
    unitPrice: 0,
  };
}

export function QiDocumentLineEditor({
  items,
  onChange,
  formatMoney,
  stockBranchId,
}: {
  items: QiLineItemRow[];
  onChange: (items: QiLineItemRow[]) => void;
  formatMoney: (value: number) => string;
  stockBranchId?: string;
}) {
  const catalogRef = useRef<Map<string, QiCatalogSelection>>(new Map());

  const updateLine = (id: string, patch: Partial<QiLineItemRow>) => {
    onChange(items.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const handleLineKindChange = (id: string, lineKind: QiLineItemRow["lineKind"]) => {
    catalogRef.current.delete(id);
    onChange(
      items.map((row) =>
        row.id === id
          ? {
              ...row,
              lineKind,
              productId: "",
              rawMaterialId: "",
              serviceId: "",
              catalogLabel: "",
              description: lineKind === "free_text" ? row.description : "",
              unit: lineKind === "free_text" ? row.unit : "",
              unitPrice: lineKind === "free_text" ? row.unitPrice : 0,
            }
          : row,
      ),
    );
  };

  const handleProductSelect = (id: string, product: QiCatalogProductDTO) => {
    const qty = items.find((row) => row.id === id)?.quantity ?? 1;
    const selection: QiCatalogSelection = { kind: "product", item: product };
    catalogRef.current.set(id, selection);
    updateLine(id, {
      productId: product.id,
      rawMaterialId: "",
      serviceId: "",
      catalogLabel: `${product.code} — ${product.name}`,
      description: product.name,
      unit: product.unit,
      unitPrice: resolveCatalogUnitPrice(selection, qty),
    });
  };

  const handleRawMaterialSelect = (
    id: string,
    rawMaterial: QiCatalogRawMaterialDTO,
  ) => {
    const qty = items.find((row) => row.id === id)?.quantity ?? 1;
    const selection: QiCatalogSelection = { kind: "raw_material", item: rawMaterial };
    catalogRef.current.set(id, selection);
    updateLine(id, {
      rawMaterialId: rawMaterial.id,
      productId: "",
      serviceId: "",
      catalogLabel: `${rawMaterial.code} — ${rawMaterial.name}`,
      description: rawMaterial.name,
      unit: rawMaterial.unit,
      unitPrice: resolveCatalogUnitPrice(selection, qty),
    });
  };

  const handleServiceSelect = (id: string, svc: QiCatalogServiceDTO) => {
    const qty = items.find((row) => row.id === id)?.quantity ?? 1;
    const selection: QiCatalogSelection = { kind: "service", item: svc };
    catalogRef.current.set(id, selection);
    updateLine(id, {
      serviceId: svc.id,
      productId: "",
      rawMaterialId: "",
      catalogLabel: `${svc.code} — ${svc.name}`,
      description: svc.name,
      unit: svc.unit,
      unitPrice: resolveCatalogUnitPrice(selection, qty),
      quantity: qty,
    });
  };

  const handleQuantityChange = (id: string, quantity: number) => {
    const cached = catalogRef.current.get(id);
    const patch: Partial<QiLineItemRow> = { quantity };
    if (cached) {
      patch.unitPrice = resolveCatalogUnitPrice(cached, quantity);
    }
    updateLine(id, patch);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="w-28 pb-2 font-medium">ປະເພດ</th>
            <th className="min-w-56 pb-2 font-medium">ລາຍການ</th>
            <th className="min-w-32 pb-2 font-medium">ລາຍລະອຽດ</th>
            <th className="w-20 pr-2 pb-2 text-right font-medium">ຈຳນວນ</th>
            <th className="w-24 pr-2 pb-2 text-right font-medium">ຫນ່ວຍ</th>
            <th className="w-28 pr-2 pb-2 text-right font-medium">ລາຄາ/ຫນ່ວຍ</th>
            <th className="w-28 pb-2 text-right font-medium">ລວມ</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const cached = catalogRef.current.get(row.id);
            const priceHint =
              cached && row.lineKind !== "free_text"
                ? resolveCatalogPriceHint(cached, row.quantity)
                : null;

            return (
              <tr key={row.id} className="border-b align-top">
                <td className="py-2 pr-2">
                  <SimpleSelect
                    value={row.lineKind}
                    onValueChange={(value) =>
                      handleLineKindChange(
                        row.id,
                        value as QiLineItemRow["lineKind"],
                      )
                    }
                    className="h-9"
                    options={[
                      { value: "product", label: "ສິນຄ້າ" },
                      { value: "raw_material", label: "ວັດຖຸດິບ" },
                      { value: "service", label: "ບໍລິການ" },
                      { value: "free_text", label: "ຂໍ້ຄວາມ" },
                    ]}
                  />
                </td>
                <td className="max-w-[min(100%,18rem)] py-2 pr-2">
                  {row.lineKind === "product" ? (
                    <div>
                      <QiProductPicker
                        value={row.productId}
                        label={row.catalogLabel || undefined}
                        onSelect={(product) => handleProductSelect(row.id, product)}
                      />
                      <QiLineAvailabilityBadge
                        productId={row.productId}
                        branchId={stockBranchId}
                        neededQty={row.quantity}
                      />
                    </div>
                  ) : row.lineKind === "raw_material" ? (
                    <QiRawMaterialPicker
                      value={row.rawMaterialId}
                      label={row.catalogLabel || undefined}
                      onSelect={(rawMaterial) =>
                        handleRawMaterialSelect(row.id, rawMaterial)
                      }
                    />
                  ) : row.lineKind === "service" ? (
                    <QiServicePicker
                      value={row.serviceId}
                      label={row.catalogLabel || undefined}
                      onSelect={(service) => handleServiceSelect(row.id, service)}
                    />
                  ) : (
                    <Input
                      value={row.description}
                      onChange={(e) =>
                        updateLine(row.id, { description: e.target.value })
                      }
                      placeholder="ລາຍການສິນຄ້າ / ບໍລິການ"
                      className="h-9"
                    />
                  )}
                </td>
                <td className="max-w-56 min-w-32 py-2 pr-2">
                  <Textarea
                    value={row.detail}
                    onChange={(e) =>
                      updateLine(row.id, { detail: e.target.value })
                    }
                    placeholder="ບໍ່ບັງຄັບ"
                    className="min-h-[52px] text-xs"
                    rows={2}
                  />
                </td>
                <td className="py-2 text-right">
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={row.quantity || ""}
                    onChange={(e) =>
                      handleQuantityChange(row.id, Number(e.target.value) || 0)
                    }
                    className="h-9 w-20 text-right"
                  />
                </td>
                <td className="py-2 pr-2">
                  <Input
                    value={row.unit}
                    onChange={(e) =>
                      updateLine(row.id, { unit: e.target.value })
                    }
                    placeholder="ຫນ່ວຍ"
                    className="h-9 w-24"
                  />
                </td>
                <td className="py-2 text-right">
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={row.unitPrice || ""}
                    onChange={(e) =>
                      updateLine(row.id, {
                        unitPrice: Number(e.target.value) || 0,
                      })
                    }
                    className="h-9 w-28 text-right"
                  />
                  {row.lineKind !== "free_text" && cached ? (
                    <p className="mt-1 text-muted-foreground text-xs">
                      {priceHint ?? "ແກ້ລາຄາໄດ້"}
                    </p>
                  ) : null}
                </td>
                <td className="py-2 text-right font-medium">
                  {formatMoney(row.quantity * row.unitPrice)}
                </td>
                <td className="py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => {
                      catalogRef.current.delete(row.id);
                      onChange(items.filter((item) => item.id !== row.id));
                    }}
                    disabled={items.length <= 1}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-[10px]">
          catalog
        </Badge>
        <span className="text-muted-foreground text-xs">
          ເລືອກສິນຄ້າ/ວັດຖຸດິບ/ບໍລິການຈາກ catalog — ລາຄາຄິດຕາມອັດຕາລາຄາຕາມຈຳນວນ
        </span>
      </div>
    </div>
  );
}

export { resolveQiProductPriceHint, resolveQiProductUnitPrice };
